import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CustomDropdown from './CustomDropdown';
import SearchableDropdown from './SearchableDropdown';
import DateRangePicker from './DateRangePicker';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface UserFolder {
  id: number;
  user_id: number;
  name: string;
  hotkey: string;
  color: string;
  created_at: string;
}

interface GenerationJob {
  id: number;
  job_name: string;
  workflow_name: string;
  workflow_id: number;
  created_at: string;
}

export interface FilterState {
  folder: string | null;
  jobId: number | null;
  jobName: string | null;
  workflow: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  search: string;
  status: 'active' | 'archived';
}

interface FilterHeaderProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  imageCounts?: {
    total: number;
    filtered: number;
  };
}

const FilterHeader: React.FC<FilterHeaderProps> = ({
  filters,
  onFiltersChange,
  imageCounts
}) => {
  const [folders, setFolders] = useState<UserFolder[]>([]);
  const [workflows, setWorkflows] = useState<string[]>([]);
  const [jobs, setJobs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchFilterData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch folders
      const foldersResponse = await axios.get(`${API_BASE}/folders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFolders(foldersResponse.data);

      // Fetch recent jobs to get workflow and job name lists
      const jobsResponse = await axios.get(`${API_BASE}/jobs?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (jobsResponse.data.jobs) {
        // Extract unique workflows
        const workflowNames = jobsResponse.data.jobs.map((job: GenerationJob) => job.workflow_name);
        const uniqueWorkflows = Array.from(new Set(workflowNames)).filter(Boolean);
        setWorkflows(uniqueWorkflows as string[]);

        // Extract unique job names
        const jobNames = jobsResponse.data.jobs.map((job: GenerationJob) => job.job_name);
        const uniqueJobs = Array.from(new Set(jobNames)).filter(Boolean);
        setJobs(uniqueJobs as string[]);
      }
    } catch (error) {
      console.error('Failed to fetch filter data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key: keyof FilterState, value: any) => {
    const newFilters = {
      ...filters,
      [key]: value
    };
    
    // Reset folder filter when changing status to prevent empty results
    if (key === 'status') {
      newFilters.folder = null;
    }
    
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    onFiltersChange({
      folder: null,
      jobId: null,
      jobName: null,
      workflow: null,
      dateFrom: null,
      dateTo: null,
      search: '', // Keep this for compatibility with FilterState interface
      status: 'active'
    });
  };

  const hasActiveFilters = () => {
    return filters.folder !== null ||
           filters.jobId !== null ||
           filters.jobName !== null ||
           filters.workflow !== null ||
           filters.dateFrom !== null ||
           filters.dateTo !== null ||
           filters.status !== 'active';
  };

  // Status dropdown options
  const statusOptions = [
    { value: 'active', label: 'Active', icon: '🟢' },
    { value: 'archived', label: 'Archived', icon: '📦' }
  ];

  // Folder dropdown options
  const folderOptions = [
    { value: '', label: 'All Folders', icon: '📁' },
    { value: 'ungrouped', label: 'Ungrouped', icon: '📂' },
    ...folders.map(folder => ({
      value: folder.name,
      label: folder.name,
      icon: '●'
    }))
  ];

  // Workflow dropdown options
  const workflowOptions = [
    { value: '', label: 'All Workflows', icon: '⚙️' },
    ...workflows.map(workflow => ({
      value: workflow,
      label: workflow,
      icon: '🔧'
    }))
  ];

  // Job dropdown options
  const jobOptions = [
    { value: '', label: 'All Jobs', icon: '💼' },
    ...jobs.map(job => ({
      value: job,
      label: job,
      icon: '📋'
    }))
  ];

  return (
    <div className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
      <div className="px-6 py-4">
        {/* Results Summary */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-bold text-gray-900">Media Gallery</h2>
            {imageCounts && (
              <div className="text-sm text-gray-600">
                Showing <span className="font-semibold text-blue-600">{imageCounts.filtered}</span> of{' '}
                <span className="font-semibold">{imageCounts.total}</span> media
              </div>
            )}
          </div>
          
          {hasActiveFilters() && (
            <button
              onClick={clearAllFilters}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
            >
              Clear All Filters
            </button>
          )}
        </div>

        {/* Filter Controls - Single Row */}
        <div className="grid grid-cols-1 lg:grid-cols-5 xl:grid-cols-11 gap-3">
          {/* Status */}
          <div className="lg:col-span-1 xl:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-2">Status</label>
            <CustomDropdown
              options={statusOptions}
              value={filters.status}
              onChange={(value) => updateFilter('status', value)}
            />
          </div>

          {/* Folder - Searchable */}
          <div className="lg:col-span-1 xl:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-2">Folder</label>
            <SearchableDropdown
              options={folderOptions}
              value={filters.folder === null ? '' : filters.folder === '' ? 'ungrouped' : filters.folder}
              onChange={(value) => {
                if (value === '') {
                  updateFilter('folder', null);
                } else if (value === 'ungrouped') {
                  updateFilter('folder', '');
                } else {
                  updateFilter('folder', value);
                }
              }}
              placeholder="All Folders"
            />
          </div>

          {/* Workflow - Searchable */}
          <div className="lg:col-span-1 xl:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-2">Workflow</label>
            <SearchableDropdown
              options={workflowOptions}
              value={filters.workflow || ''}
              onChange={(value) => updateFilter('workflow', value || null)}
              disabled={loading}
              placeholder="All Workflows"
            />
          </div>

          {/* Jobs - Searchable */}
          <div className="lg:col-span-1 xl:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-2">Jobs</label>
            <SearchableDropdown
              options={jobOptions}
              value={filters.jobName || ''}
              onChange={(value) => updateFilter('jobName', value || null)}
              disabled={loading}
              placeholder="All Jobs"
            />
          </div>

          {/* Date Range - Merged with Quick Filters */}
          <div className="lg:col-span-1 xl:col-span-3">
            <label className="block text-xs font-medium text-gray-700 mb-2">Date Range</label>
            <DateRangePicker
              dateFrom={filters.dateFrom}
              dateTo={filters.dateTo}
              onChange={(dateFrom, dateTo) => {
                const newFilters = { ...filters, dateFrom, dateTo };
                onFiltersChange(newFilters);
              }}
            />
          </div>
        </div>

        {/* Active Filters Pills */}
        {hasActiveFilters() && (
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
            <span className="text-xs text-gray-600 font-medium">Active filters:</span>

            {filters.folder !== null && (
              <div className="flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-100 to-blue-50 text-blue-800 rounded-full text-xs font-medium border border-blue-200">
                <svg className="w-3 h-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <span>
                  {filters.folder === '' ? 'Ungrouped' : filters.folder}
                </span>
                <button
                  onClick={() => updateFilter('folder', null)}
                  className="ml-2 text-blue-600 hover:text-blue-800 hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {filters.workflow && (
              <div className="flex items-center px-3 py-1.5 bg-gradient-to-r from-green-100 to-green-50 text-green-800 rounded-full text-xs font-medium border border-green-200">
                <svg className="w-3 h-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{filters.workflow}</span>
                <button
                  onClick={() => updateFilter('workflow', null)}
                  className="ml-2 text-green-600 hover:text-green-800 hover:bg-green-200 rounded-full p-0.5 transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {filters.jobName && (
              <div className="flex items-center px-3 py-1.5 bg-gradient-to-r from-teal-100 to-teal-50 text-teal-800 rounded-full text-xs font-medium border border-teal-200">
                <svg className="w-3 h-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>{filters.jobName}</span>
                <button
                  onClick={() => updateFilter('jobName', null)}
                  className="ml-2 text-teal-600 hover:text-teal-800 hover:bg-teal-200 rounded-full p-0.5 transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {(filters.dateFrom || filters.dateTo) && (
              <div className="flex items-center px-3 py-1.5 bg-gradient-to-r from-orange-100 to-orange-50 text-orange-800 rounded-full text-xs font-medium border border-orange-200">
                <svg className="w-3 h-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>
                  {filters.dateFrom ? new Date(filters.dateFrom).toLocaleDateString() : '...'} - {filters.dateTo ? new Date(filters.dateTo).toLocaleDateString() : '...'}
                </span>
                <button
                  onClick={() => {
                    const newFilters = { ...filters, dateFrom: null, dateTo: null };
                    onFiltersChange(newFilters);
                  }}
                  className="ml-2 text-orange-600 hover:text-orange-800 hover:bg-orange-200 rounded-full p-0.5 transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {filters.status !== 'active' && (
              <div className="flex items-center px-3 py-1.5 bg-gradient-to-r from-red-100 to-red-50 text-red-800 rounded-full text-xs font-medium border border-red-200">
                <svg className="w-3 h-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                <span>{filters.status}</span>
                <button
                  onClick={() => updateFilter('status', 'active')}
                  className="ml-2 text-red-600 hover:text-red-800 hover:bg-red-200 rounded-full p-0.5 transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterHeader;
